import React, { useEffect, useState } from "react";
import { Divider, Form } from "antd";
import {
  SaveButton,
  InputText,
  CancelButton,
  ModalComponent,
  trans,
  SelectWithSearch,
  SelectMultiWithSearch,
  SelectResourceList
} from "@comps/components";
import { makeRequestStateless, getErrorProps } from "@utils/helpers";
import { getCitiesByStateReq, getStatesByCountryReq } from "../requests";
import { Commons, Const } from "../../constants";
import { createSelectList } from "@comps/commonFunctions";
import {
  getRuleTypeOptions,
  normalizeHolidayRule,
  sanitizeHolidayRule
} from "../ruleHelpers";

const langKey = Const["holiday"].lngKey;

const emptyTargetValues = {
  religion_id: null,
  location_id: null,
  branch_ids: [],
  country_id: null,
  state_id: null,
  city_id: null,
  department_ids: [],
  employment_ids: [],
};

const HolidayRuleFormModal = (props) => {
  const [saveLoader, setSaveLoader] = useState(false);
  const [errors] = useState([]);
  const [isType, setIsType] = useState("");
  const [ruleTypes, setRuleTypes] = useState([]);
  const [religions, setReligions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!props.relatedData) {
      return;
    }

    setRuleTypes(getRuleTypeOptions(props.relatedData.holiday_rule_types));
    setReligions(createSelectList(props.relatedData.religions || []));
    setLocations(createSelectList(props.relatedData.locations || []));
    setAllBranches(props.relatedData.branches || []);
    setCountries(createSelectList(props.relatedData.countries || []));
    setDepartments(createSelectList(props.relatedData.departments || []));

    if (props.record) {
      const normalizedRule = normalizeHolidayRule(props.record);
      setIsType(normalizedRule.type);
      setBranches(getBranchesByLocation(normalizedRule.location_id, props.relatedData.branches || []));
      form.setFieldsValue(normalizedRule);

      if (normalizedRule.country_id) {
        makeRequestStateless(getStatesByCountryReq, normalizedRule.country_id, onGetStatesReq, null);
      }
      if (normalizedRule.state_id) {
        makeRequestStateless(getCitiesByStateReq, normalizedRule.state_id, onGetCitiesReq, null);
      }
    }
  }, [props.relatedData, props.record]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBranchesByLocation = (locationId, sourceBranches = allBranches) => {
    return createSelectList((sourceBranches || []).filter((branch) => locationId && branch.location_id === locationId));
  };

  const clearTargetFields = () => {
    form.setFieldsValue(emptyTargetValues);
    setBranches([]);
    setStates([]);
    setCities([]);
  };

  const onChangeType = (value) => {
    setIsType(value);
    clearTargetFields();
  };

  const onChangeCountry = (id) => {
    form.setFieldsValue({ state_id: null, city_id: null });
    setStates([]);
    setCities([]);
    if (id) {
      makeRequestStateless(getStatesByCountryReq, id, onGetStatesReq, null);
    }
  };

  const onGetStatesReq = (response) => {
    setStates(createSelectList(response.all_states || []));
  };

  const onChangeState = (id) => {
    form.setFieldsValue({ city_id: null });
    setCities([]);
    if (id) {
      makeRequestStateless(getCitiesByStateReq, id, onGetCitiesReq, null);
    }
  };

  const onGetCitiesReq = (response) => {
    setCities(createSelectList(response.cities || []));
  };

  const onChangeLocation = (id) => {
    form.setFieldsValue({ branch_ids: [] });
    setBranches(getBranchesByLocation(id));
  };

  const onSubmit = (data) => {
    const payload = sanitizeHolidayRule({ ...props.record, ...data });
    setSaveLoader(true);
    props.onCompleted(payload, props.record ? "Updated" : "Added");
  };

  const footer = [
    !props.disabled && <SaveButton loading={ saveLoader } form="holidayRule" key="create_button" htmlType="submit" />,
    <CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />
  ].filter(Boolean);

  return (
    <ModalComponent
      top={ 20 }
      width={ 1000 }
      maskClosable={ false }
      mainTitle={ trans(Commons[props.disabled ? "view" : props.record ? "update" : "create"]) }
      subTitle={ trans(`${langKey}.rule`) }
      visible={ true }
      footer={ footer }
      onCancel={ () => props.onCompleted(false) }
    >
      <Form { ...formLayout } name="holidayRule" onFinish={ onSubmit } form={ form } disabled={ props.disabled }>
        <Form.Item
          name="name"
          rules={ rules.name }
          label={ trans(Commons.name) }
          className="da-mb-16"
          { ...getErrorProps(errors["name"]) }
          size="small"
        >
          <InputText placeholder={ trans(`${langKey}.name`) } />
        </Form.Item>

        <Form.Item
          name="type"
          rules={ rules.type }
          label={ trans(`${langKey}.rule.type`) }
          className="da-mb-16"
          { ...getErrorProps(errors["type"]) }
          size="small"
        >
          <SelectWithSearch
            placeholder={ trans(`${langKey}.rule.type`) }
            onChange={ onChangeType }
            options={ ruleTypes }
          />
        </Form.Item>

        <Divider>Fields</Divider>

        { isType === "religion" && (
          <Form.Item
            name="religion_id"
            rules={ rules.religion_id }
            label={ trans(`${langKey}.religion_id`) }
            className="da-mb-16"
            { ...getErrorProps(errors["religion_id"]) }
            size="small"
          >
            <SelectWithSearch placeholder={ trans(`${langKey}.religion_id`) } options={ religions } />
          </Form.Item>
        ) }

        { isType === "organization_structure" && (
          <>
            <Form.Item
              name="location_id"
              rules={ rules.location_id }
              label={ trans(`${langKey}.location_id`) }
              className="da-mb-16"
              { ...getErrorProps(errors["location_id"]) }
              size="small"
            >
              <SelectWithSearch
                placeholder={ trans(`${langKey}.location_id`) }
                options={ locations }
                onChange={ onChangeLocation }
              />
            </Form.Item>
            <Form.Item
              name="branch_ids"
              rules={ rules.branch_ids }
              label={ trans(`${langKey}.branch_ids`) }
              className="da-mb-16"
              { ...getErrorProps(errors["branch_ids"]) }
              size="small"
            >
              <SelectMultiWithSearch placeholder={ trans(`${langKey}.branch_ids`) } options={ branches } />
            </Form.Item>
          </>
        ) }

        { isType === "geo_location" && (
          <>
            <Form.Item
              name="country_id"
              rules={ rules.country_id }
              label={ trans(`${langKey}.country_id`) }
              className="da-mb-16"
              { ...getErrorProps(errors["country_id"]) }
              size="small"
            >
              <SelectWithSearch
                onChange={ onChangeCountry }
                placeholder={ trans(`${langKey}.country_id`) }
                options={ countries }
              />
            </Form.Item>
            <Form.Item
              name="state_id"
              rules={ rules.state_id }
              label={ trans(`${langKey}.state_id`) }
              className="da-mb-16"
              { ...getErrorProps(errors["state_id"]) }
              size="small"
            >
              <SelectWithSearch
                onChange={ onChangeState }
                placeholder={ trans(`${langKey}.state_id`) }
                options={ states }
              />
            </Form.Item>
            <Form.Item
              name="city_id"
              rules={ rules.city_id }
              label={ trans(`${langKey}.city_id`) }
              className="da-mb-16"
              { ...getErrorProps(errors["city_id"]) }
              size="small"
            >
              <SelectWithSearch placeholder={ trans(`${langKey}.city_id`) } options={ cities } />
            </Form.Item>
          </>
        ) }

        { isType === "department" && (
          <Form.Item
            name="department_ids"
            rules={ rules.department_ids }
            label={ trans(`${langKey}.department_ids`) }
            className="da-mb-16"
            { ...getErrorProps(errors["department_ids"]) }
            size="small"
          >
            <SelectMultiWithSearch placeholder={ trans(`${langKey}.department_ids`) } options={ departments } />
          </Form.Item>
        ) }

        { isType === "employee" && (
          <Form.Item
            name="employment_ids"
            rules={ rules.employment_ids }
            label={ trans(`${langKey}.employment_ids`) }
            className="da-mb-16"
            { ...getErrorProps(errors["employment_ids"]) }
            size="small"
          >
            <SelectResourceList type="employment" mode="multiple" />
          </Form.Item>
        ) }
      </Form>
    </ModalComponent>
  );
};

export default HolidayRuleFormModal;

const rules = {
  name: [
    { required: true, message: trans(`${langKey}.name.req`) },
  ],
  type: [
    { required: true, message: trans(`${langKey}.rule.type.req`) },
  ],
  religion_id: [
    { required: true, message: trans(`${langKey}.religion_id`) },
  ],
  location_id: [
    { required: true, message: trans(`${langKey}.location_id`) },
  ],
  branch_ids: [
    { required: true, message: trans(`${langKey}.branch_ids`) },
  ],
  country_id: [
    { required: true, message: trans(`${langKey}.country_id`) },
  ],
  state_id: [
    { required: true, message: trans(`${langKey}.state_id`) },
  ],
  city_id: [
    { required: true, message: trans(`${langKey}.city_id`) },
  ],
  department_ids: [
    { required: true, message: trans(`${langKey}.department_ids`) },
  ],
  employment_ids: [
    { required: true, message: trans(`${langKey}.employment_ids`) },
  ],
};

const formLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14, offset: 2 },
  labelAlign: "left"
};
